import axios from "axios";
import { getAccessToken } from "./authService";
import { FileIndex, UploadResponse, UserSession } from "../types";

export const listFiles = async () => {
  const response = await axios.get("/api/files", {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return response.data.files as FileIndex[];
};

export const getFileUrl = async (fileName: string) => {
  const response = await axios.get(`/api/files/${fileName}`, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return response.data;
};

export const getFile = async (fileName: string) => {
  const signedUrl = await getFileUrl(fileName);
  const fileRequest = await axios.get(signedUrl);
  return fileRequest.data;
};

export const getPhotoAlbumUrls = async (album: string) => {
  const response = await axios.get(`/api/files/${album}/children`, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return response.data.urls as string[];
};

export const getUploadUrl = async (
  fileList: FileList,
  parentId: string | null,
) => {
  const response = await axios.post(
    "/api/files",
    {
      objects: Array.from(fileList).map((f) => ({
        fileName: f.name,
        isDirectory: false,
        parentFolder: parentId,
        // Send the browser-detected MIME type so the object is stored with the
        // correct Content-Type. Omit when empty so the API falls back to a
        // guess from the extension.
        contentType: f.type || undefined,
      })),
    },
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    },
  );
  return response.data.results as UploadResponse[];
};

export const createNewFolder = async (folderName: string, isAlbum: boolean) => {
  const response = await axios.post(
    "/api/files",
    {
      objects: [
        { fileName: folderName, isDirectory: true, photoAlbum: isAlbum },
      ],
    },
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    },
  );
  return response.data;
};

export const uploadFile = async (
  file: File,
  uploadUrl: string,
  contentType: string,
  progressCallback: (progress: number) => void,
) => {
  try {
    await axios.put(uploadUrl, file, {
      // Must byte-for-byte match the ContentType the API signed the presigned
      // PUT with, or S3 rejects it with SignatureDoesNotMatch.
      headers: { "Content-Type": contentType },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.floor(
            (progressEvent.loaded / progressEvent.total) * 100,
          );
          progressCallback(progress);
        }
      },
    });
    return `Success!! Uploaded ${file.name}`;
  } catch (error: any) {
    return error.response?.data || "Upload failed";
  }
};

export const renameFile = async (oldName: string, newName: string) => {
  const response = await axios.put(
    `/api/files/${oldName}`,
    { newFileName: newName },
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    },
  );
  return response.data;
};

export const assignToFolder = async (item: FileIndex, folder: FileIndex) => {
  const response = await axios.put(
    `/api/files/assign-parent`,
    {
      fileName: item.fileName,
      parentId: folder.fileName,
    },
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    },
  );
  return response.status === 200;
};

export const deleteFile = async (name: string) => {
  const response = await axios.delete(`/api/files/${name}`, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return response.data;
};

export const getUserSession = async () => {
  try {
    const response = await axios.get(`/api/files/session`, {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    });
    return {
      autoplay: response.data.autoplay,
      playedIndices: response.data.playedIndices,
      searchText: response.data.searchText,
      selectedFileNames: response.data.selectedFileNames ?? [],
      shuffle: response.data.shuffle,
      typeFilter: response.data.typeFilter,
      playedSeconds: Number(response.data.playedSeconds),
    } as UserSession;
  } catch (error) {
    return {
      autoplay: false,
      playedIndices: [],
      searchText: "",
      selectedFileIndex: -1,
      shuffle: false,
      typeFilter: "all",
      playedSeconds: 0,
      selectedFileNames: [],
    } as UserSession;
  }
};

export const saveUserSession = async (data: UserSession) => {
  const response = await axios.post(
    `/api/files/session`,
    {
      selectedFileNames: data.selectedFileNames,
      searchText: data.searchText,
      typeFilter: data.typeFilter,
      autoplay: data.autoplay,
      shuffle: data.shuffle,
      playedIndices: data.playedIndices,
      playedSeconds: data.playedSeconds.toString(),
    },
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    },
  );
  return response.data as UserSession;
};

// eslint-disable-next-line
export default {
  listFiles,
  getFileUrl,
  getFile,
  getPhotoAlbumUrls,
  getUploadUrl,
  uploadFile,
  createNewFolder,
  renameFile,
  assignToFolder,
  deleteFile,
  saveUserSession,
  getUserSession,
};
